import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

export default function Contatti() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    azienda: '',
    servizio: '',
    messaggio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    // Validazione
    if (!formData.nome.trim() || !formData.email.trim() || !formData.messaggio.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Compila tutti i campi obbligatori');
      setIsSubmitting(false);
      return;
    }

    // Validazione email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Inserisci un indirizzo email valido');
      setIsSubmitting(false);
      return;
    }

    // Validazione lunghezza messaggio
    if (formData.messaggio.length > 500) {
      setSubmitStatus('error');
      setErrorMessage('Il messaggio non può superare i 500 caratteri');
      setIsSubmitting(false);
      return;
    }

    try {
      // Configurazione EmailJS
      const templateParams = {
        from_name: formData.nome,
        from_email: formData.email,
        phone: formData.telefono || 'Non specificato',
        company: formData.azienda || 'Non specificata',
        service: formData.servizio || 'Non specificato',
        message: formData.messaggio,
        to_email: 'OrviaClient@gmail.com'
      };

      // Invia email tramite EmailJS
      await emailjs.send(
        'service_zyc0jxi', // Service ID fornito dall'utente
        'template_fbpkfhh', // Template ID fornito dall'utente
        templateParams,
        'cPpjIimiZPaLjbU4a' // Public Key fornito dall'utente
      );

      setSubmitStatus('success');
      setFormData({
        nome: '',
        email: '',
        telefono: '',
        azienda: '',
        servizio: '',
        messaggio: ''
      });
    } catch (error) {
      console.error('Errore invio email:', error);
      setSubmitStatus('error');
      setErrorMessage('Errore nell\'invio del messaggio. Riprova più tardi o contattaci direttamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* New Background */}
      <div className="fixed inset-0 z-0 bg-gradient-to-br from-[#36090d] via-[#5a0f15] to-[#36090d]">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#e97e89] rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#f1b2b9] rounded-full filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#e97e89] rounded-full filter blur-3xl animate-pulse delay-500"></div>
        </div>
      </div>

      {/* Content Overlay */}
      <div className="relative z-10">
        {/* Navigation - Sticky */}
        <nav className={`fixed top-0 left-0 right-0 z-50 shadow-md border-b border-white/20 backdrop-blur-md transition-all duration-300 ${
          isScrolled ? 'bg-[#36090d]/90 h-14' : 'bg-transparent h-16'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`flex justify-between items-center transition-all duration-300 ${
              isScrolled ? 'h-14' : 'h-16'
            }`}>
              <div className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/448d3dd4920f14db0013cfc0f3d75a62/51fa198fcc5d92a2ecc7c07e6e02f260.png"
                  alt="Orvia Logo"
                  className={`transition-all duration-300 ${
                    isScrolled ? 'h-8' : 'h-10'
                  }`}
                />
              </div>
              
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-8">
                  <a href="/" className="text-white/80 hover:text-[#e97e89] px-3 py-2 text-sm font-medium transition-colors">
                    Home
                  </a>
                  <a href="/chi-siamo" className="text-white/80 hover:text-[#e97e89] px-3 py-2 text-sm font-medium transition-colors">
                    Chi Siamo
                  </a>
                  <a href="/contatti" className="text-white hover:text-[#e97e89] px-3 py-2 text-sm font-medium transition-colors">
                    Contatti
                  </a>
                </div>
              </div>

              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-white hover:text-[#e97e89] p-2"
                >
                  <i className="ri-menu-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-[#36090d]/95 backdrop-blur-md">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-white/20">
                <a href="/" className="text-white/80 hover:text-[#e97e89] block px-3 py-2 text-base font-medium">
                  Home
                </a>
                <a href="/chi-siamo" className="text-white/80 hover:text-[#e97e89] block px-3 py-2 text-base font-medium">
                  Chi Siamo
                </a>
                <a href="/contatti" className="text-white block px-3 py-2 text-base font-medium">
                  Contatti
                </a>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 pt-32">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl lg:text-6xl font-extrabold text-white mb-6 leading-tight tracking-wider uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Contattaci
            </h1>
            
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed"
               style={{ fontFamily: 'Lato, sans-serif' }}>
              Siamo qui per trasformare le tue idee in realtà digitali. Contattaci per una consulenza gratuita
            </p>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div className="p-8 rounded-2xl shadow-xl border border-white/20 hover:bg-[#e97e89]/15 hover:border-[#e97e89]/50 transition-all duration-300">
                <h2 className="text-2xl font-extrabold text-white mb-6 tracking-wider uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Richiedi un preventivo gratuito
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nome" className="block text-sm font-medium text-white mb-2">
                        Nome *
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-white/30 rounded-xl focus:ring-2 focus:ring-[#e97e89] focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                        placeholder="Il tuo nome"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-white/30 rounded-xl focus:ring-2 focus:ring-[#e97e89] focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                        placeholder="la-tua-email@esempio.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-white mb-2">
                        Telefono
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-white/30 rounded-xl focus:ring-2 focus:ring-[#e97e89] focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                        placeholder="+39-3518712315"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="azienda" className="block text-sm font-medium text-white mb-2">
                        Azienda
                      </label>
                      <input
                        type="text"
                        id="azienda"
                        name="azienda"
                        value={formData.azienda}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-white/30 rounded-xl focus:ring-2 focus:ring-[#e97e89] focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-white/60"
                        placeholder="Nome della tua azienda"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="servizio" className="block text-sm font-medium text-white mb-2">
                      Servizio di interesse
                    </label>
                    <select
                      id="servizio"
                      name="servizio"
                      value={formData.servizio}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-white/30 rounded-xl focus:ring-2 focus:ring-[#e97e89] focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white pr-8"
                    >
                      <option value="" className="text-black">Seleziona un servizio</option>
                      <option value="sviluppo-web" className="text-black">Sviluppo Web</option>
                      <option value="design-ux-ui" className="text-black">Design UX/UI</option>
                      <option value="copywriting" className="text-black">Copywriting</option>
                      <option value="consulenza" className="text-black">Consulenza Generale</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="messaggio" className="block text-sm font-medium text-white mb-2">
                      Messaggio *
                    </label>
                    <textarea
                      id="messaggio"
                      name="messaggio"
                      value={formData.messaggio}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-white/30 rounded-xl focus:ring-2 focus:ring-[#e97e89] focus:border-transparent text-sm bg-white/10 backdrop-blur-sm resize-none text-white placeholder-white/60"
                      placeholder="Raccontaci del tuo progetto e dei tuoi obiettivi..."
                    />
                    <p className="text-xs text-white/60 mt-1">
                      {formData.messaggio.length}/500 caratteri
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || formData.messaggio.length > 500}
                    className="w-full bg-[#e97e89] text-white px-8 py-4 rounded-2xl font-semibold hover:bg-[#36090d] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? 'Invio in corso...' : 'Invia richiesta'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-500/20 border border-green-400 text-green-300 px-4 py-3 rounded-xl">
                      ✅ Messaggio inviato con successo! Ti contatteremo presto.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-500/20 border border-red-400 text-red-300 px-4 py-3 rounded-xl">
                      ❌ {errorMessage}
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div className="p-8 rounded-2xl shadow-xl border border-white/20 hover:bg-[#36090d]/25 hover:border-[#36090d]/50 transition-all duration-300">
                  <h3 className="text-xl font-extrabold text-white mb-6 tracking-wide uppercase"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Informazioni di contatto
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#e97e89]/20 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <i className="ri-mail-line text-xl text-[#e97e89]"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Email</h4>
                        <p className="text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                          OrviaClient@gmail.com
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#e97e89]/20 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <i className="ri-phone-line text-xl text-[#e97e89]"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Telefono</h4>
                        <p className="text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                          +39-3518712315
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#e97e89]/20 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <i className="ri-time-line text-xl text-[#e97e89]"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Orari di lavoro</h4>
                        <p className="text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                          Lun - Ven: 9:00 - 18:00<br />
                          Sab: 9:00 - 13:00
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8 rounded-2xl shadow-xl border border-white/20 hover:bg-[#e97e89]/15 hover:border-[#e97e89]/50 transition-all duration-300">
                  <h3 className="text-xl font-extrabold text-white mb-6 tracking-wide uppercase"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Seguici sui social
                  </h3>
                  
                  <div className="flex space-x-4">
                    <a href="https://www.instagram.com/orvia.it?igsh=bGJwNXVva3VxdTM4" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#e97e89] rounded-full flex items-center justify-center hover:bg-[#36090d] transition-colors cursor-pointer">
                      <i className="ri-instagram-line text-white"></i>
                    </a>
                    <a href="https://www.tiktok.com/@orvia.com?_r=1&_t=ZN-93GUTAQtSeE" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#e97e89] rounded-full flex items-center justify-center hover:bg-[#36090d] transition-colors cursor-pointer">
                      <i className="ri-tiktok-line text-white"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-6 tracking-wider uppercase"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Contattaci Online
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed"
                 style={{ fontFamily: 'Lato, sans-serif' }}>
                Siamo sempre disponibili per rispondere alle tue domande e discutere del tuo progetto
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex flex-col sm:flex-row gap-4">
                <a href="mailto:OrviaClient@gmail.com" className="bg-[#e97e89] text-white px-8 py-4 rounded-2xl font-semibold hover:bg-[#36090d] transition-all duration-300 whitespace-nowrap cursor-pointer text-center shadow-md hover:shadow-lg transform hover:-translate-y-1">
                  <i className="ri-mail-line mr-2"></i>
                  Invia una Email
                </a>
                <a href="tel:+393518712315" className="border-2 border-[#e97e89] text-[#e97e89] px-8 py-4 rounded-2xl font-semibold hover:bg-[#e97e89] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer text-center shadow-md hover:shadow-lg transform hover:-translate-y-1">
                  <i className="ri-phone-line mr-2"></i>
                  Chiamaci Ora
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-6 tracking-wider uppercase"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Domande frequenti
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed"
                 style={{ fontFamily: 'Lato, sans-serif' }}>
                Risposte alle domande più comuni sui nostri servizi
              </p>
            </div>

            <div className="space-y-6">
              <div className="p-6 rounded-2xl shadow-md border border-white/20 hover:bg-[#e97e89]/15 hover:border-[#e97e89]/50 transition-all duration-300">
                <h3 className="text-lg font-extrabold text-white mb-3 tracking-wide uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Quanto tempo serve per sviluppare un sito web?
                </h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                  I tempi variano in base alla complessità del progetto. Un sito vetrina richiede 2-4 settimane, 
                  mentre un e-commerce personalizzato può richiedere 6-12 settimane. Ti forniremo una timeline dettagliata dopo la consulenza iniziale.
                </p>
              </div>

              <div className="p-6 rounded-2xl shadow-md border border-white/20 hover:bg-[#36090d]/25 hover:border-[#36090d]/50 transition-all duration-300">
                <h3 className="text-lg font-extrabold text-white mb-3 tracking-wide uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Offrite servizi di manutenzione post-lancio?
                </h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Sì, offriamo pacchetti di manutenzione che includono aggiornamenti di sicurezza, backup automatici, 
                  ottimizzazioni delle performance e supporto tecnico continuo.
                </p>
              </div>

              <div className="p-6 rounded-2xl shadow-md border border-white/20 hover:bg-[#e97e89]/15 hover:border-[#e97e89]/50 transition-all duration-300">
                <h3 className="text-lg font-extrabold text-white mb-3 tracking-wide uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Posso gestire autonomamente il contenuto del sito?
                </h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Assolutamente! Tutti i nostri siti includono un CMS intuitivo che ti permette di aggiornare contenuti, 
                  immagini e pagine in autonomia. Forniamo anche formazione per l'utilizzo.
                </p>
              </div>

              <div className="p-6 rounded-2xl shadow-md border border-white/20 hover:bg-[#36090d]/25 hover:border-[#36090d]/50 transition-all duration-300">
                <h3 className="text-lg font-extrabold text-white mb-3 tracking-wide uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Quanto costa un sito web professionale?
                </h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
                  I costi dipendono dalle funzionalità richieste. Un sito vetrina parte da €2.500, 
                  mentre progetti più complessi hanno prezzi personalizzati. Contattaci per un preventivo gratuito e dettagliato.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-white py-16 border-t border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <img 
                  src="https://static.readdy.ai/image/448d3dd4920f14db0013cfc0f3d75a62/51fa198fcc5d92a2ecc7c07e6e02f260.png"
                  alt="Orvia Logo"
                  className="h-8 mb-4"
                />
                <p className="text-white/80 mb-6 leading-relaxed"
                   style={{ fontFamily: 'Lato, sans-serif' }}>
                  La tua agenzia digitale di fiducia per lo sviluppo web, design e marketing digitale. 
                  Trasformiamo le tue idee in successi online.
                </p>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/orvia.it?igsh=bGJwNXVva3VxdTM4" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#e97e89] rounded-full flex items-center justify-center hover:bg-[#36090d] transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-white"></i>
                  </a>
                  <a href="https://www.tiktok.com/@orvia.com?_r=1&_t=ZN-93GUTAQtSeE" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#e97e89] rounded-full flex items-center justify-center hover:bg-[#36090d] transition-colors cursor-pointer">
                    <i className="ri-tiktok-line text-white"></i>
                  </a>
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-4 tracking-wide uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Servizi
                </h4>
                <ul className="space-y-2 text-white/80">
                  <li><a href="/" className="hover:text-[#e97e89] transition-colors cursor-pointer">Sviluppo Web</a></li>
                  <li><a href="/" className="hover:text-[#e97e89] transition-colors cursor-pointer">Design UX/UI</a></li>
                  <li><a href="/" className="hover:text-[#e97e89] transition-colors cursor-pointer">Social Media</a></li>
                  <li><a href="/" className="hover:text-[#e97e89] transition-colors cursor-pointer">SEO & Marketing</a></li>
                  <li><a href="/" className="hover:text-[#e97e89] transition-colors cursor-pointer">Copywriting</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold mb-4 tracking-wide uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Contatti
                </h4>
                <ul className="space-y-2 text-white/80">
                  <li className="flex items-center">
                    <i className="ri-mail-line mr-2"></i>
                    OrviaClient@gmail.com
                  </li>
                  <li className="flex items-center">
                    <i className="ri-phone-line mr-2"></i>
                    +39-3518712315
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-white/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-white/60 text-sm">
                © 2024 Orvia. Tutti i diritti riservati.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-white/60 hover:text-[#e97e89] text-sm transition-colors cursor-pointer">
                  Privacy Policy
                </a>
                <a href="#" className="text-white/60 hover:text-[#e97e89] text-sm transition-colors cursor-pointer">
                  Cookie Policy
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
